<template>
  <bimini-default-section class="menu--section-bimini_aperitifs menu--section-wine_list" :menu="menu" :section="section"
                          menu-item-component="wine-list-item"
                          ></bimini-default-section>
</template>

<script lang="ts">
/**
 * Custom component for Bimini Apéritifs menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.0.0
 * @copyright  Copyright (C) 2022-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2022-05-25 (1.0.0) (jhoward)
 *    - Copy from Romanza_dessert.vue component to Bimini_aperitifs.vue component.
 *
 *  2023-04-17 (1.1.0) (jhoward)
 *    - <Bimini_aperitifs> component uses TypeScript.
 *    - <Bimini_aperitifs> requires the section prop.
 *    - Remove unused methods from <Bimini_aperitifs>.
 *
 *  2023-06-21 (1.2.0) (jhoward)
 *    - Add support for section.ms_show_pricing_header in <Bimini_aperitifs>.
 *
 *  2023-08-23 (1.3.0) (jhoward)
 *    - Use <menu-version> in <Bimini_aperitifs>.
 *
 *  2025-02-11 (1.4.0) (jhoward)
 *    - Add <menu-schedules> to the <Bimini_aperitifs> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <bimini-default-section> in <bimini_aperitifs>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import BiminiDefaultSection from "./Bimini_defaultSection.vue";

export default defineComponent({
  components: {
    BiminiDefaultSection
  },
  name: "Bimini_aperitifs",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
})
</script>

<style scoped>
.menu--section-bimini_aperitifs {
  margin: 0 auto;
  max-width: 80%;
  padding-top: 0.5em;
}

.visual_menu--section.menu--section-bimini_aperitifs {
  font-size: 0.9em;
}

.textual_menu--section.menu--section-bimini_aperitifs {
  max-width: 768px;
}

.visual_menu--container-landscape .menu--section-bimini_aperitifs {
  padding-top: 0.5em;
}

.menu--section-bimini_aperitifs:deep(h2 > span:first-child) {
  opacity: 0;
}

.menu--section-bimini_aperitifs:deep(h2 > span:last-child) {
  opacity: 1;
}

.menu--section-bimini_aperitifs:deep(.menu--item_title),
.menu--section-bimini_aperitifs:deep(.menu--section_group_title) {
  font-size: 0.9em;
  font-weight: bold;
}

.menu--section-bimini_aperitifs:deep(.menu--item_description),
.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title) {
  font-weight: normal;
  font-size: 0.8em;
  margin-top: -0.3em;
}

.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title) {
  font-weight: bold;
  font-size: 1em;
}

.menu--section.menu--section-bimini_aperitifs:deep(.menu--item_subgroup) {
  grid-gap: 0;
}

/*.menu--section-bimini_aperitifs:deep(.menu--item),
.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title) {
  display: grid;
  grid-template-columns: 1fr 0.2fr;
  grid-gap: 0.25em;
}*/

.menu--section-bimini_aperitifs:deep(.menu--item_subgroup + .menu--item_subgroup) {
  margin-top: 1em;
}

.menu--section-bimini_aperitifs:deep(.menu--item_price) {
  text-align: right;
  font-weight: bold;
}

.menu--section-bimini_aperitifs:deep(.menu--item_price) {
  font-weight: normal;
}

.menu--section-bimini_aperitifs:deep(.visual_menu--page_section.visual_menu--page_section-bottom) {
  padding-top: 2em;
}

.menu--section-bimini_aperitifs:deep(.menu--section_title),
.menu--section-bimini_aperitifs:deep(.menu--section_subtitle),
.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title) {
  color: #3e2603;
  margin: 2rem auto 0.2em auto;
  padding: 0;
  text-align: center;
}

@media (prefers-contrast: more) {
  .menu--section-bimini_aperitifs:deep(.menu--section_title),
  .menu--section-bimini_aperitifs:deep(.menu--section_subtitle),
  .menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title) {
    color: #000;
  }
}

.menu--section-bimini_aperitifs:deep(.menu--section_title),
.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title span:first-child) {
  font-family: bodoni-urw, serif;
  font-weight: 500;
  font-style: normal;
  font-variant: small-caps;
  letter-spacing: 0.05em;
  font-size: 2em;
}

.menu--section-bimini_aperitifs:deep(.menu--section_subtitle) {
  margin-top: -1em;
  font-size: 1em;
  font-weight: 400;
  margin-bottom: 0.4em;
}

.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title) {
  text-align: left;
  position: relative;
}

.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title span:first-child) {
  font-size: 1.6em;
}

.menu--section-bimini_aperitifs:deep(.menu--section-group_by_price .menu--section_subgroup_title) {
  border-bottom: none;
  color: #000000;
  font-size: 0.8em;
}

.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title) {
  align-items: end;
}

.menu--section-bimini_aperitifs:deep(.menu--section_subgroup_title span:not(:first-child)) {
  text-align: right;
  color: #000000;
  padding-bottom: 0.1em;
}

.menu--section-bimini_aperitifs:deep(.menu--item + .menu--item) {
  padding-top: 0.15em;
}

.menu--section-bimini_aperitifs:deep(.visual_menu--page_section-bottom) {
  text-align: center;
}


.menu--section-bimini_aperitifs:deep(.menu--item_subgroup .menu--section_subgroup_title span:nth-child(n + 2)) {
  display: initial;
  font-weight: normal;
}

.menu--section-bimini_aperitifs:deep(.viewport) {
  max-width: 768px;
}
</style>